// ANGULAR
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  UrlSegment,
} from '@angular/router';
import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';

// API
import {
  IWHBreadcrumbModel,
  WHBreadcrumbService,
  WHRouteZeroENUM,
  WHLoginDataService,
  WHTeamDataService,
  WHTeamDOM,
  WHDomEventService,
  WHActiveUserDOM,
  WHPermissionKeyENUM,
  WHIconENUM,
  WHDomEvent,
  WHSafeTranslateService,
} from '@workheld/workheld-shared-lib';

// RxJS 6
import { of, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { CheckUnsavedComponent } from 'src/app/app-shared/guards/check-unsaved.guard';
import { FormReferenceService } from 'src/app/app-services-helper/form-reference.service';
import { debounceTime, map, switchMap } from 'rxjs/operators';

export const ROUTE_NAME = {
  OVERVIEW: 'nav.overview.root',
  WORK: 'nav.work.root',
  MASTERDATA: 'nav.masterdata.root',
  HUMANRESOURCES: 'nav.humanresources.root',
  EQUIPMENT: 'nav.equipment.root',
  MATERIAL: 'nav.material.root',
  SHIFT: 'shift.label.shiftModels',
};

@Component({
  selector: 'app-w-h-content-page',
  templateUrl: './w-h-content-page.component.html',
  standalone: false,
})
export class WHContentPageComponent
  implements OnInit, OnDestroy, CheckUnsavedComponent
{
  // VAR
  public permissions: string[];
  public basePath: string = '';
  public routeWhitelist: string[] = [WHRouteZeroENUM.PROFILE];
  public breadcrumbs = signal<IWHBreadcrumbModel[]>([]);

  // TEAMS
  public displayFilter: boolean = false;
  public teamFilterWhitelist: string[] = [
    WHRouteZeroENUM.PROJECTS,
    WHRouteZeroENUM.CASES,
    WHRouteZeroENUM.STANDING_ORDER,
    WHRouteZeroENUM.HOME,
    WHRouteZeroENUM.TEAM_PLANNER,
    WHRouteZeroENUM.HR_WORKERS,
    WHRouteZeroENUM.JOB_REQUIREMENTS,
    WHRouteZeroENUM.SHIFT_DEFINITIONS,
    WHRouteZeroENUM.SHIFT_CALENDAR,
    WHRouteZeroENUM.SHIFT_PLANNER,
    WHRouteZeroENUM.EQUIPMENT_PLANNER,
    WHRouteZeroENUM.INFOSCREEN,
    WHRouteZeroENUM.SHIFT_BOOK,
  ];

  public isOpen: boolean = false;
  public selectedTeamDOM: WHTeamDOM = undefined;
  public cssPrefix = 'team-dropdown';
  public teamDOMList = signal<WHTeamDOM[]>([]);
  public filteredTeamDOMList: WHTeamDOM[] = [];
  // SEARCH CONTROL
  public searchCtrl: FormControl = new FormControl();

  // SUBSCRIPTION
  private subscription: Subscription = new Subscription();

  // STATE
  private activeUser: WHActiveUserDOM = null;
  canRemoveFilter = signal(false);

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private loginDataService: WHLoginDataService,
    private teamDataService: WHTeamDataService,
    private domEventService: WHDomEventService,
    private breadcrumbService: WHBreadcrumbService,
    private formReferenceService: FormReferenceService,
    private safeTranslateService: WHSafeTranslateService,
    private titleService: Title,
  ) {}

  checkUnsaved() {
    return this.formReferenceService.unsavedChanges$.pipe(
      switchMap((isDirty) => {
        if (isDirty) {
          const dialog = this.formReferenceService.createDialog();
          return dialog.pipe((discard) => {
            return discard;
          });
        } else {
          return of(true);
        }
      }),
    );
  }

  public onClickOutside() {
    this.isOpen = false;
  }

  public selectionChange(teamDOM: WHTeamDOM) {
    this.selectedTeamDOM = teamDOM;
    this.domEventService.domEvents$.next({
      type: 'teamFilterChange',
      payload: teamDOM,
    });
    this.isOpen = false;
  }

  ngOnInit(): void {
    this.formReferenceService.unsavedChanges = false;
    // TEAMS
    this.subscription.add(
      this.teamDataService.teams$.subscribe((teams: WHTeamDOM[]) => {
        this.teamDOMList.set(
          [...teams].sort((a: WHTeamDOM, b: WHTeamDOM) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) {
              return -1;
            }
            if (a.name.toLowerCase() > b.name.toLowerCase()) {
              return 1;
            }
            return 0;
          }),
        );
        this.filteredTeamDOMList = this.teamDOMList();

        if (this.teamDOMList()?.length == 1) {
          this.selectedTeamDOM = this.teamDOMList()[0];
        }
      }),
    );

    this.subscription.add(
      this.loginDataService.permissions$.subscribe((permissions: string[]) => {
        this.permissions = permissions;
      }),
    );

    this.subscription.add(
      this.domEventService.domEvents$.subscribe((domEvent: WHDomEvent) => {
        if (
          domEvent &&
          (domEvent.type === 'teamFilterChange' ||
            domEvent.type === 'teamFilterRemoved')
        ) {
          this.selectedTeamDOM = domEvent.payload;
        }
      }),
    );

    this.subscription.add(
      this.loginDataService.activeUserDOM$.subscribe(
        (user: WHActiveUserDOM) => {
          this.activeUser = user;

          this.canRemoveFilter.set(
            this.activeUser?.team ? !this.activeUser.team.subcontractor : true,
          );
        },
      ),
    );

    this.subscription.add(
      this.router.events.subscribe((event: any) => {
        if (event.routerEvent && event.routerEvent instanceof NavigationEnd) {
          const urlSegment: UrlSegment[] = event.routerEvent.url
            .split('/')
            // Filters status overview and and url parameters
            .filter((element) => element && element.split('?')[0] !== '')
            .map((e) => {
              const url = e.split('?');
              const parameters = url[1] ? url[1].split('&') : [];
              return {
                path: url[0],
                parameters: parameters,
              } as UrlSegment;
            });
          this._fillCrumbs(urlSegment);
        }
      }),
    );

    this.subscription.add(
      this.activatedRoute.url.subscribe((urlSegment: UrlSegment[]) => {
        this._fillCrumbs(urlSegment);
      }),
    );

    this.subscription.add(
      this.breadcrumbService.breadcrumbs$
        .pipe(
          debounceTime(300),
          map((breadcrumbs: IWHBreadcrumbModel[]) => {
            this.breadcrumbs.set(breadcrumbs);
            let title =
              breadcrumbs.length > 1 ? 'Workheld Flow | ' : 'Workheld Flow';
            breadcrumbs.map((b, index) => {
              title += b.nameI18nKey?.includes('.')
                ? this.safeTranslateService.instant(b.nameI18nKey)
                : b.nameI18nKey;
              if (index + 1 < breadcrumbs.length) title += ' | ';
            });
            this.titleService.setTitle(title);
          }),
        )
        .subscribe(),
    );
  }

  private _fillCrumbs(urlSegment: UrlSegment[]) {
    if (urlSegment[0]?.path) {
      this.basePath = urlSegment[0].path;
    }

    const crumbs: IWHBreadcrumbModel[] = this.breadcrumbService
      .initCrumbDOMList(urlSegment)
      .filter((crumb: IWHBreadcrumbModel) => {
        return crumb.enabled;
      });

    const baseCrumbs: IWHBreadcrumbModel[] = this.setBaseCrumbs(crumbs[0]);
    const flowCrumbs: IWHBreadcrumbModel[] = baseCrumbs.concat(crumbs);

    this.showTeamFilter(flowCrumbs);

    this.breadcrumbService.breadcrumbs$.next(flowCrumbs);
  }

  onSearchTerm(value: string) {
    if (!value || value.length === 0) {
      this.filteredTeamDOMList = this.teamDOMList();
    } else {
      this.filteredTeamDOMList = this.teamDOMList()?.filter(
        (teamDOM: WHTeamDOM) => {
          return teamDOM.name.toLowerCase().includes(value.toLowerCase());
        },
      );
    }
  }
  public showTeamFilter(flowCrumbs: IWHBreadcrumbModel[]) {
    if (flowCrumbs[1] && flowCrumbs.length === 2) {
      if (this.teamFilterWhitelist.includes(flowCrumbs[1].urlSegmentPath)) {
        this.displayFilter = true;
      } else {
        this.displayFilter = false;
      }
    } else {
      this.displayFilter = false;
    }
  }

  public get routeWhitelisted() {
    return this.routeWhitelist.includes(this.basePath);
  }

  public get coordinatorAccess() {
    return this.permissions.includes(
      WHPermissionKeyENUM.FLOW_COORDINATOR_ACCESS,
    );
  }

  public navigate(crumb: IWHBreadcrumbModel): void {
    if (!crumb.routeUrl) {
      return;
    }
    if (typeof crumb.routeUrl === 'function') {
      (crumb as any).routeUrl();
      return;
    }
    this.router.navigateByUrl(crumb.routeUrl);
  }

  private setBaseCrumbs(crumb: IWHBreadcrumbModel): IWHBreadcrumbModel[] {
    switch (crumb ? crumb.urlSegmentPath : null) {
      case WHRouteZeroENUM.HOME:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.STATUS_OVERVIEW,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.PROJECT_OVERVIEW:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.PROJECT_OVERVIEW,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.WORK_OBJECT_MATERIALUSAGE_REPORT:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.WORK_OBJECT_MATERIALUSAGE_REPORT,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.WORK_OBJECT_COMPLETION_REPORT:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.WORK_OBJECT_COMPLETION_REPORT,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case '/equipmentreport':
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: '/equipmentreport',
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.WORK_OBJECT_REPORTS_OVERVIEW:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.WORK_OBJECT_REPORTS_OVERVIEW,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.TIME_REPORT:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.TIME_REPORT,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.WORK_OBJECT_EFFORT_REPORT:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.WORK_OBJECT_EFFORT_REPORT,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.DAILY_DASHBOARD:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.OVERVIEW,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.DAILY_DASHBOARD,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      // WORK
      case WHRouteZeroENUM.PROJECTS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.WORK,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.PROJECTS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.CASES:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.WORK,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.CASES,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.STANDING_ORDER:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.WORK,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.STANDING_ORDER,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.MAINTENANCE_NOTIFICATIONS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.WORK,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.MAINTENANCE_NOTIFICATIONS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.ENTRIES_UNASSIGNED:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.WORK,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.ENTRIES_UNASSIGNED,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.CASES_CATEGORY:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.WORK,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.CASES_CATEGORY,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.TEAM_PLANNER:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.WORK,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.TEAM_PLANNER,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      // SHIFT MANAGEMENT
      case WHRouteZeroENUM.SHIFT_PLANNER:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.SHIFT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.SHIFT_PLANNER,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.INFOSCREEN:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.SHIFT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.INFOSCREEN,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.SHIFT_GROUPS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.SHIFT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.SHIFT_GROUPS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.SHIFT_DEFINITIONS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.SHIFT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.SHIFT_DEFINITIONS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.SHIFT_CALENDAR:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.SHIFT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.SHIFT_CALENDAR,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.SHIFT_BOOK:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.SHIFT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.SHIFT_BOOK,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      // Equipment
      case WHRouteZeroENUM.EQUIPMENTS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.EQUIPMENT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.EQUIPMENTS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.EQUIPMENT_PLANNER:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.EQUIPMENT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.EQUIPMENT_PLANNER,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.MAINTENANCE_PLAN:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.EQUIPMENT,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.MAINTENANCE_PLAN,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      // MATERIALS
      case WHRouteZeroENUM.MATERIALS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.MATERIAL,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.MATERIALS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.MATERIALINVENTORY:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.MATERIAL,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.MATERIALINVENTORY,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      // MASTERDATA
      case WHRouteZeroENUM.CUSTOMERS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.MASTERDATA,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.CUSTOMERS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.CHECKLIST_DEFINITION:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.MASTERDATA,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.CHECKLIST_DEFINITION,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.WORK_OBJECT_TEMPLATES:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.MASTERDATA,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.WORK_OBJECT_TEMPLATES,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.EXECUTION_GROUPS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.MASTERDATA,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.EXECUTION_GROUPS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.TOOLS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.MASTERDATA,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.TOOLS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      // HUMAN RESOURCES
      case WHRouteZeroENUM.HR_WORKERS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.HUMANRESOURCES,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.HR_WORKERS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.ABSENCE_REQUESTS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.HUMANRESOURCES,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.ABSENCE_REQUESTS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.JOB_REQUIREMENTS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.HUMANRESOURCES,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.JOB_REQUIREMENTS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      case WHRouteZeroENUM.QUALIFICATIONS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.HUMANRESOURCES,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.QUALIFICATIONS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];

      case WHRouteZeroENUM.SKILLS:
        return [
          {
            enabled: true,
            nameI18nKey: ROUTE_NAME.HUMANRESOURCES,
            routeUrl: WHRouteZeroENUM.STATUS_OVERVIEW,
            urlSegmentPath: WHRouteZeroENUM.SKILLS,
            icon: WHIconENUM.WorkheldIcon,
          },
        ];
      default:
        return [];
    }
  }

  removeFilter() {
    if (!this.canRemoveFilter()) return;

    this.selectedTeamDOM = null;
    this.domEventService.domEvents$.next({
      type: 'teamFilterChange',
      payload: this.selectedTeamDOM,
    });
  }

  public get filterIcon(): string {
    return WHIconENUM.FilterIcon as string;
  }

  public get cancelIcon(): string {
    return WHIconENUM.CancelIcon as string;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
